import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  Divider,
  FormHelperText,
  Input
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { addNews, updateNews, deleteNews, NEWS_CATEGORIES, getAllNews } from '../../services/newsService';
import { format } from 'date-fns';

function NewsManagement() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    paragraphs: [''],
    file: null,
    category: NEWS_CATEGORIES.NOTICE
  });
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const newsData = await getAllNews();
      setNews(newsData.map(doc => ({
        ...doc,
        paragraphs: doc.content.split('\n\n')
      })));
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (newsItem = null) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setFormData({
        title: newsItem.title,
        paragraphs: newsItem.paragraphs,
        category: newsItem.category,
        file: null
      });
    } else {
      setEditingNews(null);
      setFormData({
        title: '',
        paragraphs: [''],
        category: NEWS_CATEGORIES.NOTICE,
        file: null
      });
    }
    setOpenDialog(true);
    setFileError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNews(null);
    setFormData({
      title: '',
      paragraphs: [''],
      category: NEWS_CATEGORIES.NOTICE,
      file: null
    });
  };

  const handleOpenDeleteDialog = (newsItem) => {
    setNewsToDelete(newsItem);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setNewsToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleParagraphChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      paragraphs: prev.paragraphs.map((p, i) => i === index ? value : p)
    }));
  };

  const handleAddParagraph = () => {
    setFormData(prev => ({
      ...prev,
      paragraphs: [...prev.paragraphs, '']
    }));
  };

  const handleRemoveParagraph = (index) => {
    if (formData.paragraphs.length > 1) {
      setFormData(prev => ({
        ...prev,
        paragraphs: prev.paragraphs.filter((_, i) => i !== index)
      }));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setFileError('Please select an image or PDF file');
      return;
    }

    // Validate file size
    const maxSize = file.type.startsWith('image/') ? 1024 * 1024 : 3 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError(`File size should not exceed ${file.type.startsWith('image/') ? '1MB' : '3MB'}`);
      return;
    }

    setFormData(prev => ({ ...prev, file }));
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const newsData = {
        title: formData.title,
        content: formData.paragraphs.join('\n\n'),
        category: formData.category
      };

      if (editingNews) {
        await updateNews(editingNews.id, newsData, formData.file);
      } else {
        await addNews(newsData, formData.file);
      }
      handleCloseDialog();
      loadNews();
    } catch (error) {
      console.error('Error saving news:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteNews(newsToDelete.id);
      handleCloseDeleteDialog();
      loadNews();
    } catch (error) {
      console.error('Error deleting news:', error);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#F5F5F5',
      pt: '20px',
      pb: 8,
      ml: { xs: 0, sm: '250px' }
    }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              color: '#580100'
            }}
          >
            News Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              bgcolor: '#580100',
              '&:hover': {
                bgcolor: '#400000'
              }
            }}
          >
            Add News
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress sx={{ color: '#580100' }} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {news.map((item) => (
              <Grid item xs={12} md={4} key={item.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography 
                        variant="h6" 
                        component="h3"
                        sx={{ 
                          color: '#580100',
                          fontWeight: 600,
                          flex: 1
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Box>
                        <IconButton 
                          onClick={() => handleOpenDialog(item)} 
                          size="small"
                          sx={{ color: '#580100' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleOpenDeleteDialog(item)} 
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {item.timestamp ? format(new Date(item.timestamp), 'MMMM dd, yyyy') : 'Date not available'}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {item.paragraphs[0]}
                    </Typography>
                    {item.attachment && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton
                          size="small"
                          href={item.attachment.url}
                          target="_blank"
                          sx={{ color: '#580100', mr: 1 }}
                        >
                          {item.attachment.type === 'application/pdf' ? (
                            <PictureAsPdfIcon />
                          ) : (
                            <ImageIcon />
                          )}
                        </IconButton>
                        <Typography variant="caption" color="text.secondary">
                          {item.attachment.name}
                        </Typography>
                      </Box>
                    )}
                    <Button
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => handleOpenDialog(item)}
                      sx={{ 
                        color: '#580100',
                        '&:hover': {
                          bgcolor: 'rgba(88,1,0,0.1)'
                        }
                      }}
                    >
                      View & Edit
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editingNews ? 'Edit News' : 'Add News'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                name="title"
                label="Title"
                type="text"
                fullWidth
                value={formData.title}
                onChange={handleInputChange}
                required
                sx={{ mb: 3 }}
              />
              <TextField
                select
                margin="dense"
                name="category"
                label="Category"
                fullWidth
                value={formData.category}
                onChange={handleInputChange}
                required
                sx={{ mb: 3 }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value={NEWS_CATEGORIES.NOTICE}>Notice</option>
                <option value={NEWS_CATEGORIES.NOTIFICATION}>Notification</option>
                <option value={NEWS_CATEGORIES.CIRCULAR}>Circular</option>
              </TextField>
              
              <Stack spacing={2}>
                {formData.paragraphs.map((paragraph, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      margin="dense"
                      label={`Paragraph ${index + 1}`}
                      multiline
                      rows={3}
                      fullWidth
                      value={paragraph}
                      onChange={(e) => handleParagraphChange(index, e.target.value)}
                      required
                    />
                    {formData.paragraphs.length > 1 && (
                      <IconButton 
                        color="error" 
                        onClick={() => handleRemoveParagraph(index)}
                        sx={{ mt: 1 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
              
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddParagraph}
                sx={{ mt: 2 }}
              >
                Add Paragraph
              </Button>
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mt: 2 }}>
                <Input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                  sx={{ display: 'none' }}
                  id="file-input"
                />
                <label htmlFor="file-input">
                  <Button
                    component="span"
                    startIcon={<AttachFileIcon />}
                    variant="outlined"
                    sx={{ 
                      color: '#580100',
                      borderColor: '#580100',
                      '&:hover': {
                        borderColor: '#400000',
                        bgcolor: 'rgba(88,1,0,0.1)'
                      }
                    }}
                  >
                    Attach File
                  </Button>
                </label>
                {formData.file && (
                  <Typography variant="caption" sx={{ ml: 2 }}>
                    Selected: {formData.file.name}
                  </Typography>
                )}
                {fileError && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {fileError}
                  </FormHelperText>
                )}
                <FormHelperText sx={{ mt: 1 }}>
                  Upload an image (max 1MB) or PDF (max 3MB)
                </FormHelperText>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                sx={{ 
                  bgcolor: '#580100',
                  '&:hover': {
                    bgcolor: '#400000'
                  }
                }}
              >
                {editingNews ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete News</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{newsToDelete?.title}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button 
              onClick={handleDelete} 
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default NewsManagement;
